import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Modal,
  Fade,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PaidIcon from '@mui/icons-material/Paid';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { DevWinBadge, DevTabBadge } from '../DevPageBadge';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '96vw',
  maxWidth: 760,
  maxHeight: '92vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 3,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const PENDING_COLOR = '#ed6c02';
export const PAID_COLOR = '#2e7d32';

export const fmtMoney = (n) => (n || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

// รายการเงินที่รอจ่ายของ budget เดียว — ใช้ร่วมกันระหว่าง BudgetTable.js
// และ ProjectDashboardTable.js (เปิดจาก column AP รอจ่าย)
export default function PendingItemsModal({ open, budget, onClose, onUpdated }) {
  const dispatch = useDispatch();
  const [localBudget, setLocalBudget] = useState(budget);
  const [pendingTab, setPendingTab] = useState(0); // 0 = ยังไม่จ่าย, 1 = จ่ายแล้ว
  const [pendingForm, setPendingForm] = useState({ name: '', amount: '' });
  const [pendingSaving, setPendingSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amount: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [payItem, setPayItem] = useState(null);
  const [payFile, setPayFile] = useState(null);
  const [paySaving, setPaySaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalBudget(budget);
      setPendingTab(0);
    }
  }, [open, budget]);

  const budgetId = localBudget?.id || localBudget?._id;
  const pendingItemsAll = localBudget?.pending_items || [];
  const pendingItemsUnpaid = _.filter(pendingItemsAll, (i) => !i.paid);
  const pendingItemsPaid = _.filter(pendingItemsAll, (i) => i.paid);
  const pendingItemsShown = pendingTab === 0 ? pendingItemsUnpaid : pendingItemsPaid;

  const applyUpdate = (updated) => {
    setLocalBudget(updated);
    onUpdated?.(updated);
  };

  const openAddDialog = () => {
    setPendingForm({ name: '', amount: '' });
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
    setPendingForm({ name: '', amount: '' });
  };

  const handleAddPendingItem = async () => {
    const name = pendingForm.name.trim();
    const amount = parseFloat(pendingForm.amount);
    if (!name || isNaN(amount) || amount <= 0) {
      showAlert('กรุณากรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    setPendingSaving(true);
    try {
      const updated = await dispatch(actions.budgetAddPendingItem(budgetId, { name, amount }));
      applyUpdate(updated);
      closeAddDialog();
      setPendingTab(0);
    } catch (error) {
      showAlert('เพิ่มรายการไม่สำเร็จ');
    } finally {
      setPendingSaving(false);
    }
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setEditForm({ name: item.name, amount: String(item.amount) });
  };

  const closeEditDialog = () => {
    setEditItem(null);
    setEditForm({ name: '', amount: '' });
  };

  const handleUpdatePendingItem = async () => {
    const name = editForm.name.trim();
    const amount = parseFloat(editForm.amount);
    if (!name || isNaN(amount) || amount <= 0) {
      showAlert('กรุณากรอกชื่อรายการและจำนวนเงินให้ถูกต้อง');
      return;
    }
    setEditSaving(true);
    try {
      const updated = await dispatch(actions.budgetUpdatePendingItem(budgetId, editItem._id, { name, amount }));
      applyUpdate(updated);
      closeEditDialog();
    } catch (error) {
      showAlert('แก้ไขรายการไม่สำเร็จ');
    } finally {
      setEditSaving(false);
    }
  };

  const handleRemovePendingItem = async (itemId) => {
    if (!(await confirmAsync('ลบรายการนี้?'))) return;
    try {
      const updated = await dispatch(actions.budgetRemovePendingItem(budgetId, itemId));
      applyUpdate(updated);
    } catch (error) {
      showAlert('ลบรายการไม่สำเร็จ');
    }
  };

  const openPayDialog = (item) => {
    setPayItem(item);
    setPayFile(null);
  };

  const closePayDialog = () => {
    setPayItem(null);
    setPayFile(null);
  };

  const handleConfirmPay = async () => {
    setPaySaving(true);
    try {
      let slip_url;
      if (payFile) {
        const formData = new FormData();
        formData.append('file', payFile, payFile.name);
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        slip_url = data?.url;
      }
      const updated = await dispatch(
        actions.budgetPayPendingItem(budgetId, payItem._id, slip_url ? { slip_url } : {}),
      );
      applyUpdate(updated);
      closePayDialog();
    } catch (error) {
      showAlert('บันทึกการจ่ายเงินไม่สำเร็จ');
    } finally {
      setPaySaving(false);
    }
  };

  return (
    <>
      <DevWinBadge
        num={150}
        name="PendingItemsModal · รายการเงินที่รอจ่าย"
        open={!!open}
        bottom={84}
      />
      <DevTabBadge tabNum={8} name="ยังไม่จ่าย" active={!!open && pendingTab === 0} />
      <DevTabBadge tabNum={9} name="จ่ายแล้ว" active={!!open && pendingTab === 1} />

      {/* Pending payment items modal */}
      <Modal open={!!open} onClose={onClose}>
        <Fade in={!!open}>
          <Box sx={modalStyle}>
            {/* Header + stats */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700} noWrap>
                    รายการเงินที่รอจ่าย
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.6,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1.5,
                      bgcolor: alpha(PENDING_COLOR, 0.1),
                      maxWidth: '100%',
                    }}
                  >
                    <Typography noWrap sx={{ fontWeight: 700, color: PENDING_COLOR }}>
                      {localBudget?.prefix}{localBudget?.budget_number}
                    </Typography>
                    <Typography noWrap sx={{ fontWeight: 700, color: PENDING_COLOR }}>
                      {localBudget?.name}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="เพิ่มรายการรอจ่าย">
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ bgcolor: PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#c65a00' } }}
                    >
                      เพิ่มรายการ
                    </Button>
                  </Tooltip>
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.2, mt: 2.5 }}>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(PENDING_COLOR, 0.08), border: '1px solid', borderColor: alpha(PENDING_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: PENDING_COLOR, fontWeight: 700, display: 'block' }}>
                    ยังไม่จ่าย ({pendingItemsUnpaid.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: PENDING_COLOR }}>
                    ฿{fmtMoney(_.sumBy(pendingItemsUnpaid, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: alpha(PAID_COLOR, 0.08), border: '1px solid', borderColor: alpha(PAID_COLOR, 0.3) }}>
                  <Typography variant="caption" sx={{ color: PAID_COLOR, fontWeight: 700, display: 'block' }}>
                    จ่ายแล้ว ({pendingItemsPaid.length})
                  </Typography>
                  <Typography fontWeight={700} sx={{ color: PAID_COLOR }}>
                    ฿{fmtMoney(_.sumBy(pendingItemsPaid, 'amount'))}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, borderRadius: 2, p: 1.2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block' }}>
                    รวมทั้งหมด ({pendingItemsAll.length})
                  </Typography>
                  <Typography fontWeight={700}>
                    ฿{fmtMoney(_.sumBy(pendingItemsAll, 'amount'))}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Tabs
              value={pendingTab}
              onChange={(_ev, v) => setPendingTab(v)}
              variant="fullWidth"
              sx={{ minHeight: 42, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, '& .MuiTab-root': { minHeight: 42, fontSize: '0.85rem', fontWeight: 600, py: 0.5 } }}
            >
              <Tab label={`ยังไม่จ่าย (${pendingItemsUnpaid.length})`} sx={{ color: pendingTab === 0 ? PENDING_COLOR : undefined }} />
              <Tab label={`จ่ายแล้ว (${pendingItemsPaid.length})`} sx={{ color: pendingTab === 1 ? PAID_COLOR : undefined }} />
            </Tabs>

            {/* List */}
            <Box sx={{ overflowY: 'auto', flex: 1, px: 2, py: 1.5, bgcolor: alpha('#000', 0.015) }}>
              {_.isEmpty(pendingItemsShown) ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: pendingTab === 0 ? 1.5 : 0 }}>
                    {pendingTab === 0 ? 'ยังไม่มีรายการรอจ่าย' : 'ยังไม่มีรายการที่จ่ายแล้ว'}
                  </Typography>
                  {pendingTab === 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={openAddDialog}
                      sx={{ color: PENDING_COLOR, borderColor: PENDING_COLOR }}
                    >
                      เพิ่มรายการรอจ่าย
                    </Button>
                  )}
                </Box>
              ) : (
                _.orderBy(pendingItemsShown, ['createdAt'], ['desc']).map((item) => (
                  <Box
                    key={item._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 1.5,
                      py: 1,
                      mb: 1,
                      '&:hover': { borderColor: item.paid ? PAID_COLOR : PENDING_COLOR },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(item.paid ? PAID_COLOR : PENDING_COLOR, 0.12),
                        }}
                      >
                        {item.paid
                          ? <CheckCircleIcon sx={{ fontSize: 18, color: PAID_COLOR }} />
                          : <HourglassEmptyIcon sx={{ fontSize: 18, color: PENDING_COLOR }} />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.paid
                            ? `จ่ายเมื่อ ${item.paidAt ? dayjs(item.paidAt).format('DD/MM/YYYY') : '-'}`
                            : `เพิ่มเมื่อ ${item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : '-'}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ mr: 0.5, color: item.paid ? PAID_COLOR : 'text.primary', whiteSpace: 'nowrap' }}>
                        ฿{fmtMoney(item.amount)}
                      </Typography>
                      {item.paid ? (
                        item.slip_url && (
                          <Tooltip title="ดูสลิป">
                            <IconButton size="small" onClick={() => window.open(item.slip_url, '_blank')}>
                              <AttachFileIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          sx={{ bgcolor: PENDING_COLOR, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#c65a00' } }}
                          startIcon={<PaidIcon fontSize="small" />}
                          onClick={() => openPayDialog(item)}
                        >
                          จ่ายเงิน
                        </Button>
                      )}
                      {!item.paid && (
                        <Tooltip title="แก้ไข">
                          <IconButton size="small" onClick={() => openEditDialog(item)} sx={{ color: PENDING_COLOR }}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="ลบรายการ">
                        <IconButton size="small" onClick={() => handleRemovePendingItem(item._id)} sx={{ color: 'text.disabled' }}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))
              )}
            </Box>

          </Box>
        </Fade>
      </Modal>

      {/* Add pending item dialog */}
      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddCircleOutlineIcon sx={{ color: PENDING_COLOR }} />
          เพิ่มรายการรอจ่าย
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              label="ชื่อรายการ"
              value={pendingForm.name}
              onChange={(ev) => setPendingForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
            />
            <TextField
              type="number"
              label="จำนวนเงิน"
              value={pendingForm.amount}
              onChange={(ev) => setPendingForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeAddDialog} disabled={pendingSaving}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: PENDING_COLOR, '&:hover': { bgcolor: '#c65a00' } }}
            onClick={handleAddPendingItem}
            disabled={pendingSaving}
          >
            เพิ่มรายการ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit pending item dialog */}
      <Dialog open={!!editItem} onClose={closeEditDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon sx={{ color: PENDING_COLOR }} />
          แก้ไขรายการรอจ่าย
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              label="ชื่อรายการ"
              value={editForm.name}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, name: ev.target.value }))}
              fullWidth
            />
            <TextField
              type="number"
              label="จำนวนเงิน"
              value={editForm.amount}
              onChange={(ev) => setEditForm((prev) => ({ ...prev, amount: ev.target.value }))}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeEditDialog} disabled={editSaving}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: PENDING_COLOR, '&:hover': { bgcolor: '#c65a00' } }}
            onClick={handleUpdatePendingItem}
            disabled={editSaving}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay confirmation dialog */}
      <Dialog open={!!payItem} onClose={closePayDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaidIcon sx={{ color: PAID_COLOR }} />
          ยืนยันจ่ายเงิน
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderRadius: 2, p: 1.5, mb: 2, bgcolor: alpha(PENDING_COLOR, 0.08), border: '1px solid', borderColor: alpha(PENDING_COLOR, 0.3) }}>
            <Typography variant="body2" color="text.secondary">{payItem?.name}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: PENDING_COLOR }}>
              ฿{fmtMoney(payItem?.amount)}
            </Typography>
          </Box>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<AttachFileIcon />}
            fullWidth
          >
            {payFile ? payFile.name : 'แนบสลิป (ไม่บังคับ)'}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(ev) => setPayFile(ev.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closePayDialog} disabled={paySaving}>ยกเลิก</Button>
          <Button
            variant="contained"
            disableElevation
            sx={{ bgcolor: PAID_COLOR, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={handleConfirmPay}
            disabled={paySaving}
          >
            ยืนยันจ่ายเงิน
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
